import type { ICellRendererParams, ValueGetterParams } from 'ag-grid-community';
import { get } from 'lodash-es';
import {
  getReconStatusWarningSeverity,
  isPositionWarning,
  type PositionReconStatus,
  type PositionWarning,
} from '../../../types/Position';
import type { AgGridWarningProps } from '../../AgGrid/types';
import { PositionWarnings } from '../../PositionWarnings';
import type { ColDefFactory, Column } from './types';
import { warning } from './warning';
import type { WarningColumnValue } from './warning.types';

type PositionWarningColumnValue = PositionWarning & WarningColumnValue;

export const reconWarning: ColDefFactory<Column> = column => {
  const warningColumn = warning(column);

  return {
    ...warningColumn,
    colId: 'reconWarning',
    cellRendererParams: (params: ICellRendererParams<any, WarningColumnValue | undefined>): AgGridWarningProps => {
      const positionWarning = isPositionWarning(params.value)
        ? params.value
        : isPositionWarning(params.data?.reconWarning)
          ? params.data.reconWarning
          : undefined;

      if (positionWarning != null) {
        return {
          ...params,
          tooltip: <PositionWarnings warning={positionWarning} />,
        };
      }

      // The base case where we just have a severity and no data specific data type
      if (params.value?.severity != null) {
        return params;
      }

      // If there exists no severity on the value, explicitly set value to completely undefined
      return {
        ...params,
        value: undefined,
      };
    },
    // Position.reconWarning is a class getter, but blotter websocket rows can be plain objects.
    // Derive the same WarningColumnValue from ReconStatus so direct and nested position rows aggregate consistently.
    valueGetter: (params: ValueGetterParams<unknown>): PositionWarningColumnValue | WarningColumnValue | undefined => {
      const warningValue =
        typeof warningColumn.valueGetter === 'function'
          ? (warningColumn.valueGetter(params) as WarningColumnValue | undefined)
          : undefined;
      if (warningValue?.severity != null) {
        return warningValue;
      }

      return getPositionWarningColumnValue(params, column.field);
    },
  };
};

function getPositionWarningColumnValue(
  params: ValueGetterParams<unknown>,
  field: string | undefined
): PositionWarningColumnValue | undefined {
  const source = getPositionWarningSource(params.data, field);
  if (source == null || source.ReconStatus == null) {
    return undefined;
  }
  const { ReconStatus: reconStatus } = source;

  const severity = getReconStatusWarningSeverity(reconStatus.Status);
  if (severity == null) {
    return undefined;
  }

  const nodeId = params.node?.id;
  if (!nodeId) {
    return undefined;
  }

  return {
    severity,
    asset: source.Asset,
    reconStatus,
    nodeId,
  };
}

function getPositionWarningSource(data: unknown, field: string | undefined) {
  if (data == null) {
    return undefined;
  }

  if (field?.endsWith('.reconWarning')) {
    return get(data, field.slice(0, -'.reconWarning'.length)) as
      | { Asset: string; ReconStatus?: PositionReconStatus }
      | undefined;
  }

  return data as { Asset: string; ReconStatus?: PositionReconStatus };
}
